import React from 'react';
import { useBalance } from 'wagmi';
import { getChainName } from '@soundxyz/utils/const';
import { Button } from '@components/main-components/button/Button';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import { useWalletRequest } from '@context/wallet-request';
import { useTransactionFlow } from '@hooks/useTransactionFlow';
import { AVAILABLE_CHAIN_IDS } from '@utils/constants';
import { InsufficientFundsEth, InsufficientFundsOptimism } from '../../InsufficientFundsModal';

export function PayWithETHButton({
  onBuy,
  disableCollecting,
  isBuying,
  chainId,
  totalPriceWei,
  payWithCardButton,
  payWithEthButton,
}: {
  onBuy: () => Promise<void | null>;
  disableCollecting: boolean;
  isBuying: boolean;
  chainId: number;
  totalPriceWei: string;
  payWithCardButton: React.ReactNode;
  payWithEthButton: React.ReactNode;
}) {
  const { requestRejected } = useWalletRequest();
  const { authUserAddress } = useAuth();
  const { isSwitchingNetwork, isAuthenticating } = useConnectedAuth();
  const { openModal } = ModalContext.useContainer();
  const { data: balanceData, isLoading: balanceLoading } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId,
  });
  const { data: mainnetBalance } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId: AVAILABLE_CHAIN_IDS.eth,
  });
  const { data: opBalance } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId: AVAILABLE_CHAIN_IDS.optimism,
  });
  const hasMainnetEth = !!mainnetBalance && mainnetBalance.value !== 0n;
  const hasOpEth = !!opBalance && opBalance.value !== 0n;

  const showInsufficientFunds = !!balanceData && balanceData.value <= BigInt(totalPriceWei);

  const [label, setLabel] = React.useState<
    'Pay with ETH' | 'Confirm in wallet' | 'Switch chain in wallet'
  >('Pay with ETH');

  React.useEffect(() => {
    if (requestRejected) {
      setLabel('Pay with ETH');
    }
  }, [requestRejected]);

  const handleBuy = useTransactionFlow({
    chainId,
    onChainSwitchFail() {
      setLabel('Pay with ETH');
    },
    onAuth: async () => {
      if (!balanceLoading && showInsufficientFunds) {
        switch (getChainName(chainId)) {
          case 'MAINNET':
          case 'GOERLI':
            return openModal(ModalType.INSUFFICIENT_FUNDS_ETH, {
              body: <InsufficientFundsEth wei={totalPriceWei} actionButton={payWithCardButton} />,
            });
          case 'OPTIMISM':
          case 'OPTIMISM_GOERLI':
            const hasEnoughOnMainnet =
              !!mainnetBalance && mainnetBalance.value > BigInt(totalPriceWei);
            const hasEnoughOnOptimism = opBalance && opBalance.value > BigInt(totalPriceWei);
            /**
             * Users can use Mainnet ETH to pay for NFT on Optimism.
             * - user has no optimism ETH
             * - user has mainnet ETH
             *   - user has enough balance to pay for NFT
             */
            if (!hasOpEth && hasMainnetEth && hasEnoughOnMainnet) {
              return openModal(ModalType.INSUFFICIENT_FUNDS_OPTIMISM, {
                body: (
                  <InsufficientFundsOptimism
                    wei={totalPriceWei}
                    actionButton={payWithEthButton}
                    type="Pay with ETH on Mainnet"
                  />
                ),
              });
            }

            /**
             * User can use Mainnet ETH to pay for NFT on Optimism.
             * - user does not have enough ETH on optimism
             * - user has mainnet ETH
             *   - user has enough balance to pay for NFT
             */
            if (!hasEnoughOnOptimism && hasMainnetEth && hasEnoughOnMainnet) {
              return openModal(ModalType.INSUFFICIENT_FUNDS_OPTIMISM, {
                body: (
                  <InsufficientFundsOptimism
                    wei={totalPriceWei}
                    actionButton={payWithEthButton}
                    type="Pay with ETH on Mainnet"
                  />
                ),
              });
            }

            return openModal(ModalType.INSUFFICIENT_FUNDS_OPTIMISM, {
              body: (
                <InsufficientFundsOptimism
                  wei={totalPriceWei}
                  actionButton={payWithCardButton}
                  type="Pay with Credit Card"
                />
              ),
            });
          default:
            throw new Error('Unsupported blockchain');
        }
      }

      setLabel('Confirm in wallet');

      onBuy().finally(() => {
        setLabel('Pay with ETH');
      });
    },
  });

  return (
    <Button
      variant={label !== 'Pay with ETH' ? 'primary' : 'fuchsia'}
      label={label}
      deprecatedCSS={{ justifyContent: 'flex-start' }}
      fullWidth
      size="XL"
      disabled={disableCollecting}
      loading={isBuying || isSwitchingNetwork || balanceLoading || isAuthenticating}
      className="items-center justify-between"
      onClick={handleBuy}
    />
  );
}
